import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Plus, Trash2, Save } from 'lucide-react';
import api from '../utils/api';

const HeroBannerEditor = () => {
  const [settings, setSettings] = useState({
    main_heading: '',
    sub_heading: '',
    description: '',
    checkmarks: [],
    button_text: '',
    video_enabled: false,
    video_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/hero-banner');
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch hero settings:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/hero-banner', settings);
      alert('Hero banner settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addCheckmark = () => {
    setSettings(prev => ({
      ...prev,
      checkmarks: [...prev.checkmarks, 'New checkmark item']
    }));
  };

  const removeCheckmark = (index) => {
    setSettings(prev => ({
      ...prev,
      checkmarks: prev.checkmarks.filter((_, i) => i !== index)
    }));
  };

  const updateCheckmark = (index, value) => {
    setSettings(prev => ({
      ...prev,
      checkmarks: prev.checkmarks.map((item, i) => i === index ? value : item)
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-600">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hero Banner Settings</CardTitle>
        <p className="text-sm text-gray-600">Edit the main banner section on your homepage</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Heading */}
        <div>
          <Label>Main Heading</Label>
          <Input
            value={settings.main_heading}
            onChange={(e) => setSettings({...settings, main_heading: e.target.value})}
            placeholder="Professional Email Signatures"
          />
        </div>

        {/* Sub Heading */}
        <div>
          <Label>Sub Heading (Red Text)</Label>
          <Input
            value={settings.sub_heading}
            onChange={(e) => setSettings({...settings, sub_heading: e.target.value})}
            placeholder="That Actually Work"
          />
        </div>

        {/* Description */}
        <div>
          <Label>Description</Label>
          <Textarea
            value={settings.description}
            onChange={(e) => setSettings({...settings, description: e.target.value})}
            placeholder="Create stunning, professional email signatures..."
            rows={3}
          />
        </div>

        {/* Button Text */}
        <div>
          <Label>CTA Button Text</Label>
          <Input
            value={settings.button_text}
            onChange={(e) => setSettings({...settings, button_text: e.target.value})}
            placeholder="Start Your Free 15-Day Trial"
          />
          <p className="text-xs text-gray-500 mt-1">
            This text appears on the main call-to-action button
          </p>
        </div>

        {/* Checkmarks */}
        <div>
          <Label>Checkmark Items (Below CTA Button)</Label>
          <div className="space-y-2 mt-2">
            {settings.checkmarks.map((checkmark, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={checkmark}
                  onChange={(e) => updateCheckmark(index, e.target.value)}
                  placeholder="Checkmark text"
                />
                <Button
                  onClick={() => removeCheckmark(index)}
                  variant="outline"
                  size="sm"
                  className="text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button
            onClick={addCheckmark}
            variant="outline"
            size="sm"
            className="mt-2"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Checkmark
          </Button>
        </div>

        {/* Video Settings */}
        <div className="border-t pt-6">
          <Label className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              checked={settings.video_enabled}
              onChange={(e) => setSettings({...settings, video_enabled: e.target.checked})}
              className="rounded"
            />
            <span>Enable Demo Video Button</span>
          </Label>
          
          {settings.video_enabled && (
            <div>
              <Label>Video URL (YouTube, Vimeo, etc.)</Label>
              <Input
                value={settings.video_url || ''}
                onChange={(e) => setSettings({...settings, video_url: e.target.value})}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-xs text-gray-500 mt-1">
                The "Watch Demo" button will appear next to "Start Free Trial"
              </p>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#EB2F5B] hover:bg-[#d02850]"
            size="lg"
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <p className="text-sm text-gray-500 text-center mt-2">
            Changes will appear immediately on the homepage
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default HeroBannerEditor;
